import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import moment from 'moment-timezone';

import ColumnHeader, { SortType } from '../../../../../components/ColumnHeader';
import WaitlistPlayerCard from './WaitlistPlayerCard';
import Img from '../../../../../components/Img';
import InvitedPlayerCardV3 from '../../../../../components/cards/PlayerCardV3/InvitedPlayerCardV3';
import { SPLIT_LABEL, divisionFormatTypeIds, eventStateTypeIds, paymentStatusTypeIds, playerActionOptions, registrationGroupStatusTypeIds, registrationStatusTypeIds, sportTypeIds, teamPaymentTypeIds, utrTypeIds, waitlistBucketTypeLabels, waitlistBucketTypeSortKey, waitlistBucketTypes } from '../../../../../utils/eventConstants';
import { ComparatorTypes, comparator } from '../../../../../utils/comparator';
import { areAllTeamMembersSelected, areWithdrawnPlayersSelected, isBucketUnderMaxPlayerLimit, isUserEligibleForDivision, shouldAddSplitAction } from '../../../../../utils/events';
import { ratingTypes } from '../../../../../utils/constants';
import Icon from '../../../../../components/Img/Icon';

import styles from '../../../event.scss';
import { playersDictByMemberIdShape, playerShape } from '../../../../../propTypes';

const WaitlistPlayersList = (props) => {
    const { teamList, withdrawnTeamList, selectedDivision, showPlayers, isTennisSportEvent, isPickleBallSportEvent, isPtsEvent, isCurrentMemberAdmin, showRatingNotLoggedIn, utrTypeId, isOfficialHSEvent, auth, sendMessage, playerPtsRanks, hideUtrLabel, sportTypeId, playersDictByMemberId, currentDivision, isFreeEvent, isStripe, allowOffPlatformPayments, showRegStatus, toggleMessagePopUp, showNeedPartners } = props;

    const { event, playersTabState, players } = useSelector(({ eventProfile }) => {
        const { event, playersTabState, players } = eventProfile;
        return { event, playersTabState, players };
    });
    const widthdrawnPlayers = players?.withdrawn || [];
    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [allTeamMembersSelected, setAllTeamMembersSelected] = useState(false);
    const [hasWithdrawnPlayersSelected, setHasWithdrawnPlayersSelected] = useState(false);
    const isDoubles = selectedDivision.divisionFormatTypeId === divisionFormatTypeIds.DOUBLES;

    const {
        sortParam,
        filterQuery,
    } = playersTabState || {};

    const eventSchedule = event.data?.eventSchedule || null;
    const timeZone = eventSchedule ? eventSchedule.timeZone : null;
    const eventEndDate = moment.utc(eventSchedule.eventEndUtc).tz(eventSchedule.timeZone?.value).startOf('day');
    const currentDate = moment();
    const isEventFinished = currentDate.isAfter(eventEndDate);

    const shouldShowSwitchDivision = !isEventFinished;

    const gridLayout = [
        '2fr', // position
        '6fr', // name
        ...(['2fr', '2fr']), // ratings
        ...(isPtsEvent ? ['1fr'] : []), // ptt rank
        ...(isCurrentMemberAdmin ? ['3fr'] : []), // reg.date
        ...(isCurrentMemberAdmin && isDoubles ? ['3fr'] : []), // team reg.date
        ...(showRegStatus ? ['2fr'] : []), // status
        ...(isCurrentMemberAdmin ? ['2fr'] : []), // payment
    ];

    const { teamPaymentTypeId, divisionFormatTypeId, isWaitlistLocked } = currentDivision || {};
    const isPaySeparately = teamPaymentTypeId === teamPaymentTypeIds.PAY_SEPARATELY;
    const isSinglesDivision = divisionFormatTypeId === divisionFormatTypeIds.SINGLES;
    const isPayAsTeam = teamPaymentTypeId === teamPaymentTypeIds.PAY_AS_A_TEAM;
    const isUserEligible = isUserEligibleForDivision(currentDivision, auth.user, eventSchedule.eventStartUtc, sportTypeId, event.data);
    const isRegistrationOpen = event.data?.registrationStateId === eventStateTypeIds.OPEN_REGISTRATION;
    const isPreReg = event.data?.registrationStateId === eventStateTypeIds.PRE_REGISTRATION;

    // WAITLIST BUCKETS

    let waitListTeamList = [];
    const mainBucket = [];
    const waitlistBucket = [];
    const needPartnerBucket = [];
    const getFilteredPlayersBasedOnVisibility = (teamList, showPlayers, showNeedPartners, memberId) => {
        if (!showPlayers && !showNeedPartners) {
            return teamList?.map((team) => {
                team.players = team.players.filter((player) => {
                    return player.memberId === memberId;
                });
                return team;
            });
        }
        if (!showPlayers && showNeedPartners) {
            return teamList?.map((team) => {
                team.players = team.players.filter((player) => {
                    const division = player.divisions?.find(div => selectedDivision.id === div.id && div.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER);
                    return player.memberId === memberId || division;
                });
                return team;
            });
        }
        return teamList;
    };
    const filteredTeamList = getFilteredPlayersBasedOnVisibility(teamList, showPlayers, showNeedPartners, auth.user?.id) || [];
    filteredTeamList.forEach(team => {
        if (team.players.length === 1) {
            const playerDivisionObj = team.players[0]?.divisions.find(div => div.id === selectedDivision.id);
            if (playerDivisionObj) {
                if (playerDivisionObj.registrationGroupStatus === registrationGroupStatusTypeIds.REGISTERED) {
                    if (team.invite) {
                        waitlistBucket.push({
                            ...team,
                            bucketType: waitlistBucketTypeLabels.WAITLIST,
                            position: playerDivisionObj.position,
                            order: playerDivisionObj.order,
                        });
                    } else if (isBucketUnderMaxPlayerLimit(mainBucket, selectedDivision)) {
                        mainBucket.push({
                            ...team,
                            bucketType: waitlistBucketTypeLabels.MAIN,
                            position: playerDivisionObj.position,
                            order: playerDivisionObj.order,
                        });
                    } else {
                        waitlistBucket.push({
                            ...team,
                            bucketType: waitlistBucketTypeLabels.WAITLIST,
                            position: playerDivisionObj.position,
                            order: playerDivisionObj.order,
                        });
                    }
                } else if (playerDivisionObj.registrationGroupStatus === registrationGroupStatusTypeIds.PENDING) {
                    waitlistBucket.push({
                        ...team,
                        bucketType: waitlistBucketTypeLabels.WAITLIST,
                        order: playerDivisionObj.order,
                    });
                } else if (playerDivisionObj.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER) {
                    needPartnerBucket.push({
                        ...team,
                        bucketType: waitlistBucketTypeLabels.NEED_PARTNER,
                        position: playerDivisionObj.position,
                        order: playerDivisionObj.order,
                    });
                }
            }
        } else if (team.players.length === 2) {
            const player1DivisionObj = team.players[0]?.divisions.find(div => div.id === selectedDivision.id);
            const player2DivisionObj = team.players[1]?.divisions.find(div => div.id === selectedDivision.id);

            if (player1DivisionObj && player2DivisionObj) {
                const regStatusIds = [player1DivisionObj.registrationGroupStatus, player2DivisionObj.registrationGroupStatus];

                if (regStatusIds.every(el => el === registrationGroupStatusTypeIds.REGISTERED)) {
                    if (isBucketUnderMaxPlayerLimit(mainBucket, selectedDivision)) {
                        mainBucket.push({
                            ...team,
                            bucketType: waitlistBucketTypeLabels.MAIN,
                            position: player1DivisionObj.position,
                            order: player1DivisionObj.order,
                        });
                    } else {
                        waitlistBucket.push({
                            ...team,
                            bucketType: waitlistBucketTypeLabels.WAITLIST,
                            position: player1DivisionObj.position,
                            order: player1DivisionObj.order,
                        });
                    }
                } else if (regStatusIds.some(el => el === registrationGroupStatusTypeIds.PENDING)) {
                    if (selectedDivision.allowTeamOnWaitlist) {
                        waitlistBucket.push({
                            ...team,
                            bucketType: waitlistBucketTypeLabels.WAITLIST,
                            position: player1DivisionObj.position,
                            order: player1DivisionObj.order,
                        });
                    } else if (isBucketUnderMaxPlayerLimit(mainBucket, selectedDivision)) {
                        mainBucket.push({
                            ...team,
                            bucketType: waitlistBucketTypeLabels.MAIN,
                            position: player1DivisionObj.position,
                            order: player1DivisionObj.order,
                        });
                    } else {
                        waitlistBucket.push({
                            ...team,
                            bucketType: waitlistBucketTypeLabels.WAITLIST,
                            position: player1DivisionObj.position,
                            order: player1DivisionObj.order,
                        });
                    }
                }
            }
        }
    });

    waitListTeamList = [
        ...mainBucket,
        ...waitlistBucket,
        ...needPartnerBucket,
        ...(isCurrentMemberAdmin ? withdrawnTeamList : []),
    ];

    const hasQuery = typeof filterQuery === 'string' && filterQuery.trim().length > 0;

    const filteredTeams = hasQuery ? waitListTeamList.filter(team => {
        const playerListFilter = filterQuery?.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
        const regex = new RegExp(playerListFilter, 'i');
        if (filterQuery) {
            return team.searchableName?.match(regex);
        }
        return true;
    }) : waitListTeamList;

    const selectablePlayerCount = isSinglesDivision ? filteredTeams.length : filteredTeams.reduce((acc, team) => acc + team.players.length, 0);

    const registeredCount = currentDivision.nonAcceptanceMainCount;
    const maxCount = isDoubles ? currentDivision.maxTeams : currentDivision.maxPlayers;
    const isMainBucketFull = maxCount !== null && registeredCount >= maxCount;

    const showWaitlistOnly = !isCurrentMemberAdmin && auth.user && isUserEligible && (isRegistrationOpen || isPreReg) && isWaitlistLocked;

    const playerRegStateText = isMainBucketFull || showWaitlistOnly ? 'Waitlist Only for New Registrations' : '';

    useEffect(() => {
        const teamMembersSelected = areAllTeamMembersSelected({ players: players.data, selectedPlayers, selectedDivision, isDoubles });
        setAllTeamMembersSelected(teamMembersSelected);

        // Check if any selected players are withdrawn
        const hasWithdrawn = areWithdrawnPlayersSelected({ players: players.data, selectedPlayers, selectedDivision, withdrawnPlayers: widthdrawnPlayers });
        setHasWithdrawnPlayersSelected(hasWithdrawn);
    }, [selectedPlayers, players.data, widthdrawnPlayers, selectedDivision.id, isDoubles]);

    const handleToggleAllClick = () => {
        if (selectedPlayers.length === selectablePlayerCount) {
            setSelectedPlayers([]);
        } else {
            const udpatedSelection = [];
            filteredTeams.forEach(team => team.players.forEach(player => udpatedSelection.push(player.memberId)));
            setSelectedPlayers(udpatedSelection);
        }
    };

    const onPlayerSelectClick = (memberId) => {
        let udpatedSelection = [...selectedPlayers];
        if (!selectedPlayers.includes(memberId)) {
            udpatedSelection.push(memberId);
        } else {
            udpatedSelection = udpatedSelection.filter(mid => mid !== memberId);
        }
        setSelectedPlayers(udpatedSelection);
    };

    const onSelectEveryPlayerClick = () => {
        const udpatedSelection = filteredTeams.map(p => p.memberId);
        setSelectedPlayers(udpatedSelection);
    };

    const onClearSelectionClick = () => {
        setSelectedPlayers([]);
    };

    const onMessageClick = () => {
        toggleMessagePopUp(selectedPlayers);
    };

    const onSwitchDivisionClick = () => {
        if (!allTeamMembersSelected || hasWithdrawnPlayersSelected) {
            return;
        }
        props.onSwitchDivisionClick({ memberIds: selectedPlayers, divisionId: selectedDivision.id });
    };

    const getPlayerActions = (playerDivision, bucketType) => {
        let playerActions = [];
        if (playerDivision?.registrationGroupStatus === registrationGroupStatusTypeIds.REGISTERED || playerDivision?.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER || playerDivision?.registrationGroupStatus === registrationGroupStatusTypeIds.PENDING) {
            if (isStripe || allowOffPlatformPayments || isFreeEvent) {
                playerActions = [
                    { label: 'Manage', value: 'withdraw' },
                ];
            } else {
                playerActions = [
                    { label: 'Withdraw', value: 'withdraw' },
                ];
                if (!isFreeEvent) {
                    playerActions.push({ label: 'Refund entry fees', value: 'refund' });
                }
            }
            if (currentDivision.price === 'Free' && playerDivision.paymentStatusTypeId === paymentStatusTypeIds.PAID) {
                const withdrawActionIndex = playerActions.findIndex(action => action.value === playerActionOptions.WITHDRAW);
                playerActions[withdrawActionIndex].label = 'Withdraw and Refund';
            }
            if (isCurrentMemberAdmin && currentDivision.price === 'Free' && playerDivision.paymentStatusTypeId === paymentStatusTypeIds.PAID && playersTabState?.selectedStatus?.value !== 'withdrawn') {
                const withdrawActionIndex = playerActions.findIndex(action => action.value === playerActionOptions.WITHDRAW);
                playerActions[withdrawActionIndex].label = 'Withdraw and Refund';
            }
        }
        if (bucketType === waitlistBucketTypes.WITHDRAWN && playerDivision?.paymentStatusTypeId === paymentStatusTypeIds.PAID) {
            if (isStripe) {
                playerActions = [
                    { label: 'Manage', value: 'refund' },
                ];
            } else if (!allowOffPlatformPayments) {
                playerActions = [
                    { label: 'Refund entry fees', value: 'refund' },
                ];
            }
        }

        if (playerDivision?.registrationStatusTypeId === registrationStatusTypeIds.CREATED) {
            playerActions = [
                {
                    label: `Remove ${isSinglesDivision || isPaySeparately ? 'player' : 'team'}`,
                    value: playerActionOptions.REMOVE,
                },
            ];
        }
        if (bucketType === waitlistBucketTypeLabels.NEED_PARTNER || bucketType === waitlistBucketTypes.NEED_PARTNER) {
            playerActions.push({ value: playerActionOptions.ADD_PARTNER, label: 'Add Partner' });
        }
        if (shouldAddSplitAction(isCurrentMemberAdmin, playerDivision, isPayAsTeam, isPaySeparately, currentDivision?.divisionFormatTypeId)) {
            playerActions.push({ value: playerActionOptions.SPLIT, label: SPLIT_LABEL });
        }
        if (isWaitlistLocked && playerDivision.type === waitlistBucketTypeSortKey.WAITLIST) {
            playerActions.push({ label: `Move ${isSinglesDivision ? 'Player' : 'Team'} to Main`, value: playerActionOptions.WAITLIST_MOVE_TO_MAIN });
        }
        return playerActions;
    };

    const getComparatorArguments = (srtParam, srtType) => {
        const args = [];
        const primarySortArg = {
            type: ComparatorTypes.NUMBER,
            field: 'order',
            descend: srtType === SortType.DESC,
        };
        args.push(primarySortArg);
        const secondarySortArg = {
            type: ComparatorTypes.NUMBER,
            field: 'position',
            descend: srtType === SortType.DESC,
        };
        args.push(secondarySortArg);
        return args;
    };

    filteredTeams.sort(comparator(getComparatorArguments(sortParam, SortType.ASC)));
    if (!teamList) {
        return <p data-testid="event-players.waitlist.loading-msg">Players are loading...</p>;
    }
    if (!showPlayers && waitListTeamList?.length === 0) {
        return <p data-testid="event-players.waitlist.hidden-msg">Registered players are hidden.</p>;
    }
    if (!waitListTeamList?.length) {
        return (
            <p data-testid="event-players.waitlist.empty-msg">
                Waiting for players<br />
                Check back to view players registered for this event.
            </p>
        );
    }
    return (
        <>
            <div className="fw300 fs14 d-flex aic gap-16" data-testid="event-players.waitlist.count-bar">
                Showing {filteredTeams.length} {isSinglesDivision ? 'players' : 'teams'}
                {isCurrentMemberAdmin && isWaitlistLocked && (
                    <div className="text-danger d-flex aic gap-8" data-testid="event-players.waitlist.waitlist-frozen-badge">
                        <Img src="/img/v4/lock-red.svg" /> Waitlist Frozen
                    </div>
                )}
                {!isCurrentMemberAdmin && !!playerRegStateText && (
                    <div className="text-warning d-flex aic gap-8">{playerRegStateText}</div>
                )}
            </div>
            <div className={styles.playerListMargin}>
                <div className={styles.hScroll}>
                    <div className={styles.playerListWrapper}>
                        <div className="d-flex text-small fw600 player-list-header player-list-header-v3">
                            {isCurrentMemberAdmin && (
                                <div className={`d-flex align-items-center`}>
                                    <div className="checkbox-wrapper">
                                        <div className={`checkbox-check clickable ${selectedPlayers.length === selectablePlayerCount ? 'checked' : ''}`} onClick={handleToggleAllClick} data-testid="event-players.waitlist.select-all-checkbox"><Img src="/img/icons/checkmark-icon.svg" /></div>
                                    </div>
                                </div>
                            )}
                            {selectedPlayers.length > 0 ? (
                                <div className="flex-grow">
                                    <div className="row">
                                        <span className="text-dark mr16 ml8" data-testid="event-players.waitlist.selected-count">
                                            {selectedPlayers.length} Selected
                                        </span> {selectedPlayers.length === selectablePlayerCount ? <span className="link clickable" onClick={onClearSelectionClick} data-testid="event-players.waitlist.clear-selection-btn">Clear Selection</span> : <span className="link clickable" onClick={onSelectEveryPlayerClick} data-testid="event-players.waitlist.select-all-btn">Select All Players</span>}
                                        <span className="ml16 mr16" data-testid="event-players.waitlist.separator">|</span>
                                        <span className="link clickable icon" onClick={onMessageClick} data-testid="event-players.waitlist.message-btn">
                                            <Img alt="message" src={'/img/v4/message.svg'} /> Message Selected Players
                                        </span>
                                        {shouldShowSwitchDivision && (
                                            <span
                                                className={`${allTeamMembersSelected && !hasWithdrawnPlayersSelected ? 'link' : ''} clickable ml16`}
                                                onClick={onSwitchDivisionClick}
                                                data-testid="event-players.switch-division-btn"
                                                data-tip={(() => {
                                                    const messages = [];
                                                    if (!allTeamMembersSelected) {
                                                        messages.push('All team members have to be selected to switch divisions.');
                                                    }
                                                    if (hasWithdrawnPlayersSelected) {
                                                        messages.push('Withdrawn player(s) cannot be selected to switch divisions.');
                                                    }
                                                    return messages.join(' ');
                                                })()}
                                            >
                                                <Icon src={'/img/v4/switch.svg'} /> Switch Division
                                            </span>
                                        )}
                                        <ReactTooltip place="top" type="dark" effect="solid" />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-grow">
                                    <div className="row">
                                        <div className={`col-12`}>
                                            <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.join(' ') }}>
                                                <ColumnHeader
                                                    columnName="Position"
                                                />
                                                <ColumnHeader
                                                    columnName={`Name`}
                                                />
                                                {isTennisSportEvent && (
                                                    <>
                                                        <ColumnHeader
                                                            columnName={ratingTypes.tennis}
                                                            imgUrl="/img/v3/icons/icon-singles-black.svg"
                                                        />
                                                        <ColumnHeader
                                                            columnName={ratingTypes.tennis}
                                                            imgUrl="/img/v3/icons/icon-doubles-black.svg"
                                                        />
                                                    </>
                                                )}
                                                {isPickleBallSportEvent && (
                                                    <>
                                                        <ColumnHeader
                                                            columnName={ratingTypes.pickleball}
                                                            imgUrl="/img/v3/icons/icon-singles-black.svg"
                                                        />
                                                        <ColumnHeader
                                                            columnName={ratingTypes.pickleball}
                                                            imgUrl="/img/v3/icons/icon-doubles-black.svg"
                                                        />
                                                    </>
                                                )}
                                                {isPtsEvent && (
                                                    <div className={``}>
                                                        <ColumnHeader
                                                            columnName="PTT Rank"
                                                        />
                                                    </div>
                                                )}
                                                {isCurrentMemberAdmin && (
                                                    <ColumnHeader
                                                        columnName="Reg.date"
                                                    />
                                                )}
                                                {isCurrentMemberAdmin && isDoubles && (
                                                    <ColumnHeader
                                                        columnName="Team Reg.date"
                                                    />
                                                )}
                                                {showRegStatus && (
                                                    <ColumnHeader
                                                        columnName="Status"
                                                    />
                                                )}
                                                {isCurrentMemberAdmin && (
                                                    <ColumnHeader
                                                        columnName="Payment"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="bookmark">
                                <div className="options" />
                            </div>
                        </div>
                        {filteredTeams.length > 0 && filteredTeams.map((team, i) => {
                            if (team.players.length > 0) {
                                const updatedDivision = team.players.flatMap(player => player.divisions.find(division => division.id === playersTabState.divisionId)).find(division => division);
                                return (
                                    <div
                                        className={`${styles.teamCard} ${styles.eventProfileV3}`}
                                        key={`team-${team.players?.[0]?.playerId || i}`}
                                    >
                                        {team.players.map((player, j) => {
                                            let isWithdrawn = false;
                                            let withdrawnPlayerDivision = null;
                                            widthdrawnPlayers.forEach((withdrawnPlayer) => {
                                                if (withdrawnPlayer.playerId === player.playerId) {
                                                    withdrawnPlayer.divisions.forEach((division) => {
                                                        if (division.id === playersTabState.divisionId) {
                                                            isWithdrawn = true;
                                                            withdrawnPlayerDivision = division;
                                                        }
                                                    });
                                                }
                                            });
                                            const playerDivision = player.divisions.find(div => div.id === playersTabState.divisionId);
                                            if (playerDivision || isWithdrawn) {
                                                const isOwnPlayerCard = player.memberId === auth.user?.id;
                                                const playerActions = isWithdrawn ? getPlayerActions(withdrawnPlayerDivision, team.bucketType) : getPlayerActions(playerDivision, team.bucketType);
                                                const showMenuOptions = isCurrentMemberAdmin && (j === 0 || isPaySeparately) && playerActions.length > 0;
                                                return (
                                                    <WaitlistPlayerCard
                                                        key={`player-${player.playerId || j}`}
                                                        addOptionsSpace={true}
                                                        auth={auth}
                                                        currentDivisionId={playersTabState.divisionId}
                                                        gridLayout={gridLayout}
                                                        hideRatings={false}
                                                        hideUtrLabel={hideUtrLabel}
                                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                                        isSelected={selectedPlayers.includes(player.memberId)}
                                                        menuOnSelected={(selected, e) => props.playerMenuOnSelected(e, player?.playerId, selected)}
                                                        menuOptions={showMenuOptions ? playerActions : null}
                                                        onSelectClick={onPlayerSelectClick}
                                                        player={player}
                                                        playersDictByMemberId={playersDictByMemberId}
                                                        ptsRank={isPtsEvent && (playerPtsRanks[player?.memberId] || '--')}
                                                        sendMessage={sendMessage}
                                                        showCheckbox={isCurrentMemberAdmin}
                                                        showDecimals={isCurrentMemberAdmin || isOwnPlayerCard}
                                                        showHighSchoolName={isOfficialHSEvent}
                                                        showPickleBallRatings={isPickleBallSportEvent}
                                                        showRatingNotLoggedIn={showRatingNotLoggedIn}
                                                        showRegStatus={showRegStatus}
                                                        showTennisRatings={isTennisSportEvent}
                                                        sportTypeId={sportTypeId}
                                                        utrTypeId={utrTypeId}
                                                        timeZone={timeZone}
                                                        isFirstPlayer={j === 0}
                                                        isDoubles={isDoubles}
                                                        playerDivision={isWithdrawn ? withdrawnPlayerDivision : playerDivision}
                                                    />
                                                );
                                            }
                                            return null;
                                        })}
                                        {team.invite && (
                                            <InvitedPlayerCardV3
                                                invite={team.invite}
                                                showCheckbox={isCurrentMemberAdmin}
                                                gridLayout={gridLayout}
                                                showRegStatus
                                                isPtsEvent={isPtsEvent}
                                                isCurrentMemberAdmin={isCurrentMemberAdmin}
                                                divisions={updatedDivision}
                                            />
                                        )}
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

WaitlistPlayersList.propTypes = {
    allowOffPlatformPayments: PropTypes.bool,
    auth: PropTypes.shape({
        user: PropTypes.shape({
            id: PropTypes.number.isRequired,
        }).isRequired,
    }).isRequired,
    currentDivision: PropTypes.shape({
        divisionFormatTypeId: PropTypes.number,
        price: PropTypes.string,
        teamPaymentTypeId: PropTypes.number,
    }).isRequired,
    hideUtrLabel: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    isFreeEvent: PropTypes.bool,
    isOfficialHSEvent: PropTypes.bool,
    isPickleBallSportEvent: PropTypes.bool,
    isPtsEvent: PropTypes.bool,
    isStripe: PropTypes.bool,
    isTennisSportEvent: PropTypes.bool,
    playerPtsRanks: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    playersDictByMemberId: playersDictByMemberIdShape.isRequired,
    selectedDivision: PropTypes.shape({
        id: PropTypes.number.isRequired,
        allowTeamOnWaitlist: PropTypes.bool,
        divisionFormatTypeId: PropTypes.number.isRequired,
    }).isRequired,
    sendMessage: PropTypes.func,
    showPlayers: PropTypes.bool,
    showRatingNotLoggedIn: PropTypes.bool,
    showRegStatus: PropTypes.bool,
    sportTypeId: PropTypes.number,
    teamList: PropTypes.arrayOf(PropTypes.shape({
        invite: PropTypes.shape({
            email: PropTypes.string,
            name: PropTypes.string,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            status: PropTypes.string,
        }),
        players: PropTypes.arrayOf(PropTypes.shape({
            playerId: PropTypes.number,
            memberId: PropTypes.number,
            divisions: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.number,
                registrationGroupStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                registrationStatusTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                paymentStatusTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                partners: PropTypes.arrayOf(PropTypes.number),
            })),
        })),
    })).isRequired,
    toggleMessagePopUp: PropTypes.func,
    utrTypeId: PropTypes.number,
    withdrawnTeamList: PropTypes.arrayOf(PropTypes.shape({
        players: PropTypes.arrayOf(playerShape),
    })).isRequired,
    showNeedPartners: PropTypes.bool,
    onSwitchDivisionClick: PropTypes.func.isRequired,
};
WaitlistPlayersList.defaultProps = {
    allowOffPlatformPayments: false,
    hideUtrLabel: false,
    isCurrentMemberAdmin: false,
    isFreeEvent: false,
    isOfficialHSEvent: false,
    isPickleBallSportEvent: false,
    isPtsEvent: false,
    isStripe: false,
    isTennisSportEvent: true,
    playerPtsRanks: false,
    sendMessage: () => { },
    showPlayers: false,
    showRatingNotLoggedIn: false,
    showRegStatus: false,
    sportTypeId: sportTypeIds.TENNIS,
    toggleMessagePopUp: () => { },
    utrTypeId: utrTypeIds.MYUTR,
    showNeedPartners: false,
};

export default WaitlistPlayersList;
